/**
 * El catálogo del Registro Fitosanitario (MAPA), una vez por sesión.
 *
 * 1.998 productos: 383 KB de JSON, unos 50 comprimidos (medido contra la proyección
 * real el 29-08-2026). Se piden ENTEROS y se buscan en memoria (ver
 * `searchRegistry`): una consulta por pulsación desde un invernadero con 3G son
 * cientos de milisegundos por letra, y aquí lo que se está escribiendo es el nombre
 * de un producto, letra a letra.
 *
 * CUÁNDO SE PIDE: solo con `enabled`, que quien lo usa enciende al entrar en el alta
 * manual. No al arrancar la aplicación — la mayoría de las sesiones no dan de alta
 * ningún producto, y no se les van a cobrar 383 KB por si acaso.
 *
 * SI FALLA, NO PASA NADA: `items` se queda vacío, la lista de sugerencias no aparece
 * y el formulario funciona exactamente como funcionaba antes. Esto es una ayuda, no
 * un requisito; nada de lo que se guarda depende de que el catálogo haya llegado.
 */
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { invokeEdgeWithRetry } from "@/lib/invokeEdgeWithRetry";
import type { RegistryItem } from "@/lib/registrySuggest";
import { buildRegistryIndex, type RegistryIndex } from "@/lib/registry/resolveRegistration";

/** El MAPA publica los viernes y el sync corre como mucho cada 6 días. */
const STALE_MS = 6 * 60 * 60 * 1000;

export interface UseRegistryCatalog {
  items: RegistryItem[];
  /** Solo para enseñar el hueco: se está pidiendo y todavía no hay nada. */
  loading: boolean;
  /**
   * De cuándo es la copia del MAPA (C4b): el último export que entró. Se enseña
   * como «Registro MAPA al dd/mm». null si la edge aún no lo manda.
   */
  syncedAt: string | null;
}

/** Lo que devuelve la consulta: el catálogo y de cuándo es. */
interface RegistryCatalog {
  items: RegistryItem[];
  syncedAt: string | null;
}

/** Una sola lista vacía: que «sin catálogo» no sea un array nuevo en cada render. */
const SIN_ITEMS: RegistryItem[] = [];

/** La consulta, compartida entre el gancho y quien la pide a mano al exportar. */
const REGISTRY_CATALOG_QUERY = {
  queryKey: ["phyto-registry-catalog"],
  staleTime: STALE_MS,
  gcTime: STALE_MS,
  retry: false, // invokeEdgeWithRetry ya reintenta lo que merece la pena
  queryFn: async (): Promise<RegistryCatalog> => {
    const { data, error } = await invokeEdgeWithRetry<{ items?: RegistryItem[]; synced_at?: string | null }>(
      "phyto-registry-catalog",
      { attempts: 2 },
    );
    if (error) throw error;
    return { items: data?.items ?? [], syncedAt: data?.synced_at ?? null };
  },
} as const;

export function useRegistryCatalog(enabled: boolean): UseRegistryCatalog {
  const { data, isLoading } = useQuery({
    ...REGISTRY_CATALOG_QUERY,
    enabled,
  });

  return { items: data?.items ?? SIN_ITEMS, loading: enabled && isLoading, syncedAt: data?.syncedAt ?? null };
}

/**
 * El catálogo como ÍNDICE para validar el nº de registro que se imprime (REG0,
 * 25-09-2026). Lo piden los PDF justo antes de generar: si ya estaba en caché
 * no hay viaje. Si no llega (sin red, edge caída) devuelve null y el resolvedor
 * se queda en el formato — la exportación no se para por esto.
 */
export async function fetchRegistryIndex(queryClient: QueryClient): Promise<RegistryIndex | null> {
  try {
    const { items } = await queryClient.fetchQuery(REGISTRY_CATALOG_QUERY);
    return items.length ? buildRegistryIndex(items) : null;
  } catch {
    return null;
  }
}

/**
 * El catálogo ENTERO (no el índice) para el cuaderno de campo (CQ4a): el motor
 * propone el nº por nombre y saca la sustancia activa del formulado. Misma
 * consulta y misma caché que el alta y los PDF. null si no llega: la guarda de
 * salida del cuaderno lo dice y no genera con el MAPA encendido sin comprobar.
 */
export async function fetchRegistryCatalog(queryClient: QueryClient): Promise<RegistryItem[] | null> {
  try {
    const { items } = await queryClient.fetchQuery(REGISTRY_CATALOG_QUERY);
    return items.length ? items : null;
  } catch {
    return null;
  }
}
